<template>
  <div class="flex flex-col">
    <div class="sticky top-0 z-[10] bg-white">
      <div class="p-4 border-b border-gray-200">
        <div class="flex justify-between items-center">
          <h3 class="text-sm font-medium text-gray-700">
            매도 중인 호가 ({{ asks.length }}건)
          </h3>
          <div class="flex items-center gap-4">
            <div class="flex items-center gap-2">
              <select v-model="sortBy" class="text-sm border rounded-md px-2 py-1">
                <option value="maturity">만기순</option>
                <option value="appearance">등장순</option>
                <option value="yield">민평순</option>
              </select>
            </div>
            <button @click="handleCopy" class="text-gray-600 hover:text-gray-900 flex items-center gap-2">
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span v-if="copySuccess" class="text-sm text-blue-600">복사 완료</span>
              <span v-else class="text-sm">복사</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 통합된 테이블 -->
    <div class="overflow-visible">
      <table class="min-w-full divide-y divide-gray-200 select-none">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              내용
            </th>
            <th v-if="isLowCouponFilterActive"
              class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
              쿠폰금리
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
              민평
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
              가격정보
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
              잔존만기
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
              등장시각
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-if="asks.length === 0">
            <td colspan="5" class="px-4 py-8 text-sm text-center text-gray-500">
              매도 중인 호가가 없습니다
            </td>
          </tr>
          <tr v-for="ask in sortedAsks" :key="ask.id" class="hover:bg-gray-50">
            <td class="px-4 py-3 text-sm">
              <div class="flex items-center gap-2">
                <div class="relative select-none flex-shrink-0">
                  <button @click="copyBondInfos(ask.code, ask.id)" @mouseenter="showOriginalTooltip(ask.id)"
                    class="px-1 text-gray-400 hover:text-gray-600 text-xs font-medium bg-gray-100 rounded-full"
                    :data-tooltip="tooltipText[ask.id] || ask.code">
                    code
                  </button>
                  <button v-if="isAdmin" @click="showSenderInfo(ask.id)"
                    class="ml-2 px-1 text-gray-400 hover:text-gray-600 text-xs font-medium bg-gray-100 rounded-full">
                    quoter
                  </button>
                </div>
                <span class="text-gray-600 select-text">{{
                  ask.content
                  }}</span>

                <!-- 라벨 표시 (content 바로 옆) -->
                <div class="flex items-center gap-1 flex-shrink-0 relative">
                  <!-- 적용된 라벨들 (클릭 시 라벨 선택 팝업) -->
                  <div v-for="label in getVisibleLabels(ask.id)" :key="label.id"
                    @click.stop="toggleLabelSelector(ask.id)"
                    class="w-3 h-3 rounded-full flex-shrink-0 cursor-pointer hover:scale-125 transition-transform"
                    :style="{ backgroundColor: label.colorCode }" :title="label.name"></div>

                  <!-- 추가 라벨 개수 표시 (클릭 시 라벨 선택 팝업) -->
                  <span v-if="getExtraLabelCount(ask.id) > 0" @click.stop="toggleLabelSelector(ask.id)"
                    class="text-xs text-gray-500 cursor-pointer hover:text-gray-700"
                    :title="getExtraLabelNames(ask.id)">
                    +{{ getExtraLabelCount(ask.id) }}
                  </span>

                  <!-- 라벨 추가 버튼 -->
                  <button @click.stop="toggleLabelSelector(ask.id)"
                    class="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-500 transition-colors"
                    title="라벨 추가">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                  <LabelSelector v-model="openLabelSelector[ask.id]" :credit-quote-id="ask.id" position="bottom-right"
                    @label-toggled="handleLabelToggled(ask.id)" />
                </div>

                <div class="relative flex-shrink-0">
                  <Tooltip v-if="ask.hasComplianceRisk" :text="formatBondIssuance(ask)" position="top" width="220">
                    <span
                      class="text-blue-400 hover:text-blue-600 text-xs px-2 font-medium bg-blue-100 rounded-full">!</span>
                  </Tooltip>
                </div>
              </div>
            </td>
            <td v-if="isLowCouponFilterActive" class="px-4 py-3 text-sm whitespace-nowrap">
              <span>{{ formatYield(ask.couponRate) || '-' }}</span>
            </td>
            <td class="px-4 py-3 text-sm whitespace-nowrap">{{ formatYield(ask.yield) }}</td>
            <td class="px-4 py-3 text-sm whitespace-nowrap">
              <span v-if="ask.spreadHint" :class="formatPrice(ask.spreadHint).class">
                {{ formatPrice(ask.spreadHint).text }}
              </span>
              <span v-else>-</span>
            </td>
            <td class="px-4 py-3 text-sm whitespace-nowrap text-gray-500">
              {{ formatRemainingMaturity(ask.maturityDate) }}
            </td>
            <td class="px-4 py-3 text-sm whitespace-nowrap">
              {{ formatTime(ask.appearanceTime) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 발신자 정보 모달 컴포넌트 -->
    <SenderInfoModal :show="showModal" :sender-infos="senderInfos" @close="closeModal" />
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { format, differenceInMonths } from 'date-fns';
import askService from '@/service/creditQuoteService';
import Tooltip from '@/components/common/Tooltip.vue';
import { useStore } from 'vuex';
import SenderInfoModal from './SenderInfoModal.vue';
import LabelSelector from '@/components/common/LabelSelector.vue';
import { useQuoteLabel } from '@/composables/useQuoteLabel';

export default {
  name: 'ForSaleAsksComponent',
  components: {
    Tooltip,
    SenderInfoModal,
    LabelSelector,
  },
  props: {
    asks: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  setup(props) {
    const store = useStore();
    const { setQuoteLabelIds, getQuoteLabels } = useQuoteLabel();

    const sortBy = ref('maturity');
    const copySuccess = ref(false);
    const tooltipText = ref({});
    const tooltipTimers = ref({});
    const showModal = ref(false);
    const senderInfos = ref([]);
    const openLabelSelector = ref({});

    const isLowCouponFilterActive = computed(
      () => store.state.filter?.isLowCouponActive || false
    );

    const isAdmin = computed(() => store.getters['auth/isAdmin']);

    const sortedAsks = computed(() => {
      const sorted = [...props.asks];
      if (sortBy.value === 'maturity') {
        return sorted.sort(
          (a, b) => new Date(a.maturityDate) - new Date(b.maturityDate)
        );
      } else if (sortBy.value === 'appearance') {
        return sorted.sort(
          (a, b) => new Date(b.appearanceTime) - new Date(a.appearanceTime)
        );
      } else if (sortBy.value === 'yield') {
        return sorted.sort((a, b) => parseFloat(a.yield) - parseFloat(b.yield));
      }
      return sorted;
    });

    const handleCopy = async () => {
      try {
        const text = sortedAsks.value.map(ask => ask.content).join('\n');

        await navigator.clipboard.writeText(text);
        copySuccess.value = true;
        setTimeout(() => {
          copySuccess.value = false;
        }, 2000);
      } catch (err) {
        console.error('복사 실패:', err);
      }
    };

    const showOriginalTooltip = id => {
      const ask = props.asks.find(ask => ask.id === id);
      if (ask) {
        tooltipText.value[id] = ask.code;
      }
    };

    const copyBondInfos = async (code, id) => {
      try {
        const text = `${code}`;
        await navigator.clipboard.writeText(text);

        tooltipText.value[id] = '복사되었습니다';

        if (tooltipTimers.value[id]) {
          clearTimeout(tooltipTimers.value[id]);
        }

        tooltipTimers.value[id] = setTimeout(() => {
          tooltipText.value[id] = null;
          delete tooltipTimers.value[id];
        }, 2000);
      } catch (err) {
        console.error('복사 실패:', err);
      }
    };


    const formatMaturity = date => {
      if (!date) return '-';
      const maturityDate = new Date(date);
      const today = new Date();
      const yearsDiff =
        (differenceInMonths(maturityDate, today) +
          (maturityDate.getDate() - today.getDate()) / 30.0) /
        12.0; // 월 단위를 년으로 변환

      return `${format(maturityDate, 'yy.MM.dd')} (${yearsDiff.toFixed(1)}년)`;
    };

    const formatTime = timestamp => {
      if (!timestamp) return '-';
      return format(new Date(timestamp), 'HH:mm:ss');
    };

    const formatYield = yieldValue => {
      if (!yieldValue || yieldValue === '-') return '-';
      return parseFloat(yieldValue).toFixed(3);
    };

    const formatPrice = priceInfo => {
      if (!priceInfo || priceInfo.type === 'UNDEFINED')
        return { text: '-', class: '' };
      if (priceInfo.type === 'FLAT')
        return { text: '민평', class: 'text-gray-600' };

      const isHighPrice =
        (priceInfo.won !== 0 && priceInfo.type === 'OVER') ||
        (priceInfo.bp !== 0 && priceInfo.type === 'UNDER');

      const isLowPrice =
        (priceInfo.won !== 0 && priceInfo.type === 'UNDER') ||
        (priceInfo.bp !== 0 && priceInfo.type === 'OVER');

      let text = '';
      if (priceInfo.won !== 0) {
        text = `${priceInfo.type === 'OVER' ? '+' : '-'}${priceInfo.won}원`;
      } else if (priceInfo.bp !== 0) {
        text = `${priceInfo.type === 'OVER' ? '+' : '-'}${priceInfo.bp}bp`;
      }

      return {
        text,
        class: isHighPrice ? 'text-red-500' : isLowPrice ? 'text-blue-500' : '',
      };
    };

    const formatRemainingMaturity = date => {
      if (!date) return '-';
      const maturityDate = new Date(date);
      const today = new Date();
      const monthsDiff = differenceInMonths(maturityDate, today);
      const years = Math.floor(monthsDiff / 12);
      const months = monthsDiff % 12;

      if (years === 0) return `${months}개월`;
      if (months === 0) return `${years}년`;
      return `${years}년 ${months}개월`;
    };

    const formatBondIssuance = ask => {
      if (!ask) return '';

      const createRow = (label, value) => {
        return `<div class="flex mb-1">
          <div class="w-[45px] font-bold text-gray-600">${label}</div>
          <div class="flex-1">${value}</div>
        </div>`;
      };

      let result = `<div class="py-0.5">`;
      result += createRow('코드', ask.code);
      result += createRow('종목명', ask.bondName);
      result += createRow('발행일', ask.issueDate);

      if (ask.leadManagers && ask.leadManagers.length > 0) {
        result += `<div class="border-t border-gray-200 my-2"></div>`;
        ask.leadManagers.forEach((manager, index) => {
          result += createRow(`주관사${index + 1}`, manager);
        });
      }
      if (ask.underwriters && ask.underwriters.length > 0) {
        result += `<div class="border-t border-gray-200 my-2"></div>`;
        ask.underwriters.forEach((underwriter, index) => {
          result += createRow(`인수사${index + 1}`, underwriter);
        });
      }
      result += `</div>`;

      return result;
    };

    const showSenderInfo = async askId => {
      try {
        const data = await askService.findAskChats(askId);
        senderInfos.value = data;
        showModal.value = true;
      } catch (error) {
        console.error('발신자 정보 조회 실패:', error);
      }
    };

    const closeModal = () => {
      showModal.value = false;
      senderInfos.value = [];
    };

    // 라벨 관련 함수
    const MAX_VISIBLE_LABELS = 3;

    const getVisibleLabels = (creditQuoteId) => {
      const labels = getQuoteLabels(creditQuoteId);
      return labels.slice(0, MAX_VISIBLE_LABELS);
    };

    const getExtraLabelCount = (creditQuoteId) => {
      const labels = getQuoteLabels(creditQuoteId);
      return Math.max(0, labels.length - MAX_VISIBLE_LABELS);
    };

    const getExtraLabelNames = (creditQuoteId) => {
      const labels = getQuoteLabels(creditQuoteId);
      const extraLabels = labels.slice(MAX_VISIBLE_LABELS);
      return extraLabels.map(l => l.name).join(', ');
    };

    const toggleLabelSelector = (askId) => {
      openLabelSelector.value[askId] = !openLabelSelector.value[askId];
    };

    const handleLabelToggled = () => {
      // 라벨이 토글되면 낙관적 업데이트로 이미 캐시가 갱신됨
    };

    // 호가 데이터가 변경될 때마다 라벨 ID를 캐시에 저장
    watch(
      () => props.asks,
      (newAsks) => {
        for (const ask of newAsks) {
          setQuoteLabelIds(ask.id, ask.labelIds);
        }
      },
      { immediate: true, deep: true }
    );

    return {
      sortBy,
      sortedAsks,
      handleCopy,
      copySuccess,
      showOriginalTooltip,
      copyBondInfos,
      formatMaturity,
      formatTime,
      formatYield,
      formatPrice,
      formatRemainingMaturity,
      formatBondIssuance,
      tooltipText,
      isLowCouponFilterActive,
      isAdmin,
      showSenderInfo,
      showModal,
      senderInfos,
      closeModal,
      openLabelSelector,
      getVisibleLabels,
      getExtraLabelCount,
      getExtraLabelNames,
      toggleLabelSelector,
      handleLabelToggled,
    };
  },
};
</script>

<style scoped>
.th-cell {
  @apply px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider;
}

.td-cell {
  @apply px-4 py-3 text-sm whitespace-nowrap;
}

button:active {
  transform: scale(0.98);
}

button[data-tooltip] {
  position: relative;
}

button[data-tooltip]:hover::after {
  content: attr(data-tooltip);
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  background: #333;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: pre;
  z-index: 10;
  margin-left: 8px;
  max-width: 200px;
}
</style>
